/**
 * Centralized API client for admin-webapp
 * Uses cookie-based authentication (HttpOnly cookies)
 * No need to manually add Authorization headers - cookies sent automatically
 */

import axios from 'axios';
import { installAdminSessionRefresh } from './adminSessionRefresh';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Create axios instance with default config
 */
const apiClient = axios.create({
    baseURL: API_URL,
    withCredentials: true,  // ⬅️ CRITICAL: Send cookies automatically
    headers: {
        'Content-Type': 'application/json'
    }
});

/**
 * Helper function to get CSRF token from cookie
 */
function getCsrfToken() {
    const cookieName = 'csrf-token';
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === cookieName) {
            return decodeURIComponent(value);
        }
    }
    return null;
}

/**
 * Request interceptor to add CSRF token
 */
apiClient.interceptors.request.use(
    (config) => {
        const method = config.method?.toUpperCase();
        // Add CSRF token for state-changing requests
        if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
            const csrfToken = getCsrfToken();
            if (csrfToken) {
                config.headers['X-CSRF-Token'] = csrfToken;
            }
        }
        return config;
    },
    (error) => Promise.reject(error)
);

/**
 * Response interceptor for error handling
 */
apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        return Promise.reject(error);
    }
);

installAdminSessionRefresh(apiClient);

/**
 * API request helpers
 */
export const api = {
    get: async (path, config = {}) => {
        const response = await apiClient.get(path, config);
        return response.data;
    },

    post: async (path, data = {}, config = {}) => {
        const response = await apiClient.post(path, data, config);
        return response.data;
    },

    put: async (path, data = {}, config = {}) => {
        const response = await apiClient.put(path, data, config);
        return response.data;
    },

    delete: async (path, config = {}) => {
        const response = await apiClient.delete(path, config);
        return response.data;
    }
};

export default apiClient;
